import { ExtractComponentProp } from '@approvalmax/types';
import {
    Button as ButtonOld,
    ConfirmationDropdown,
    DeleteIcon,
    Popup as PopupOld,
    SaveIcon,
    ToolbarButton,
} from '@approvalmax/ui';
import { Button, Grid, openSplitViewState, Popup, useConfirmation } from '@approvalmax/ui/src/components';
import { useAtomValue, useSetAtom } from 'jotai';
import { constants } from 'modules/common';
import { domain, State } from 'modules/data';
import { IntegrationCode } from 'modules/data/domain';
import { useDispatch, useSelector } from 'modules/react-redux';
import { TbDeleteIcon, TbSaveColorlessIcon } from 'modules/sprites';
import { usePageRedesign } from 'pages/requestForm/hooks/usePageRedesign';
import { memo, useCallback, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useInvalidateGetAmaxPayXeroRequestDetails } from 'shared/data/webApp/v1';
import { useUserBetaFeatures } from 'shared/hooks';
import { ocrIsLoadingState } from 'shared/states';

import {
    cancelActivePopup,
    deleteRequest,
    saveAndApproveRequest,
    saveAndResubmitRequest,
    saveAndSubmitRequest,
    saveDraftRequest,
    showResubmitRequestPopup,
    showValidationErrors,
} from '../../../actions';
import { CheckDuplicatesPopup, defaultSplitViewOpenState, ResubmitRequestPopupContent } from '../../../components';
import {
    CheckDuplicatesNetSuite,
    CheckDuplicatesQbooks,
    CheckDuplicatesXero,
    DuplicateCheckTypes,
} from '../../../components/CheckDuplicates';
import { OPTIMISTIC_LOCKING_POPUP } from '../../../reducers/page/activePopup/optimisticLockingPopupReducer';
import { RESUBMIT_REQUEST_POPUP } from '../../../reducers/page/activePopup/resubmitRequestPopupReducer';
import { getActivePopupId, getRequest, isLoading } from '../../../selectors/pageSelectors';
import { getEditCommands, validateRequest } from '../../../selectors/requestSelectors';
import { OptimisticLockingPopup } from '../OptimisticLockingPopup/OptimisticLockingPopup';
import { ResubmitRequestPopup } from '../ResubmitRequestPopupOld/ResubmitRequestPopup';
import { hasDuplicateCheck } from './ActionsBlock.helpers';
import { useInvalidateDuplicates } from './ActionsBlock.hooks';
import { messages } from './ActionsBlock.messages';
import { Container, ImportButton } from './ActionsBlock.styles';
import { ActionsBlockProps, ResubmitRequestParams, SubmitRequestType } from './ActionsBlock.types';

const { commonConstants } = constants;

const ActionsBlock = memo<ActionsBlockProps>((props) => {
    const {
        customGetRequestTransfer,
        customRequestValidationFn,
        customSaveAndSubmitRequest,
        onOpenImportFilePopup,
        isImportFileButtonHidden,
        isSaving = false,
        className,
    } = props;

    const ocrIsLoading = useAtomValue(ocrIsLoadingState);
    const dispatch = useDispatch();
    const commands = useSelector(getEditCommands);
    const loading = useSelector(isLoading);
    const activePopupId = useSelector(getActivePopupId);
    const request = useSelector(getRequest);
    const validationErrors = useSelector((state: State) => validateRequest(state, request));
    const invalidateDuplicates = useInvalidateDuplicates(request.id);
    const [needToCompleteReviewFlag, setNeedToCompleteReviewFlag] = useState(false);
    const [submitRequestType, setSubmitRequestType] = useState<SubmitRequestType | null>(null);
    const [resubmitCommentText, setResubmitCommentText] = useState('');
    const openSplitView = useAtomValue(openSplitViewState);
    const setDefaultSplitViewOpen = useSetAtom(defaultSplitViewOpenState);

    const qa = bemFactory.qa('reqf-actions-block');

    const invalidateGetAmaxPayXeroRequestDetails = useInvalidateGetAmaxPayXeroRequestDetails();

    const ocrAvailableRequest =
        request.integrationCode && commonConstants.ocrAvailableIntegrationCodes.includes(request.integrationCode);

    const [duplicatesCheckType, setDuplicatesCheckType] = useState<DuplicateCheckTypes>();

    const onDuplicateCheckCancel = useCallback(() => setDuplicatesCheckType(undefined), []);

    const handleSaveAndSubmitRequest = useCallback<ExtractComponentProp<typeof CheckDuplicatesXero, 'onSuccess'>>(
        (duplicates) => {
            onDuplicateCheckCancel();
            ocrAvailableRequest && setDefaultSplitViewOpen(openSplitView);

            if (customSaveAndSubmitRequest) {
                customSaveAndSubmitRequest();
            } else {
                dispatch(saveAndSubmitRequest({ customGetRequestTransfer, customRequestValidationFn }));
            }

            invalidateDuplicates(duplicates?.map((el) => el.requestId));
        },
        [
            customGetRequestTransfer,
            customRequestValidationFn,
            customSaveAndSubmitRequest,
            dispatch,
            invalidateDuplicates,
            ocrAvailableRequest,
            onDuplicateCheckCancel,
            openSplitView,
            setDefaultSplitViewOpen,
        ]
    );

    const resubmitRequestPopup = useCallback<ExtractComponentProp<typeof CheckDuplicatesXero, 'onSuccess'>>(
        async (duplicates) => {
            onDuplicateCheckCancel();

            if (customRequestValidationFn) {
                const errors = await customRequestValidationFn();

                if (errors.length > 0) {
                    return;
                }
            }

            if (validationErrors.length > 0) {
                handleSaveAndSubmitRequest(duplicates);

                return;
            }

            dispatch(showResubmitRequestPopup());
        },
        [
            onDuplicateCheckCancel,
            customRequestValidationFn,
            validationErrors.length,
            dispatch,
            handleSaveAndSubmitRequest,
        ]
    );

    const getIsValidCheck = useCallback(async () => {
        if (customRequestValidationFn) {
            const errors = await customRequestValidationFn();

            if (errors.length > 0) {
                dispatch(showValidationErrors(errors));

                return false;
            }

            return true;
        }

        if (validationErrors.length > 0) {
            dispatch(showValidationErrors(validationErrors));

            return false;
        }

        return true;
    }, [dispatch, validationErrors, customRequestValidationFn]);

    const duplicateCheckSubmit = useCallback(async () => {
        if (!hasDuplicateCheck(request.integrationCode)) {
            handleSaveAndSubmitRequest();

            return;
        }

        const isValid = await getIsValidCheck();

        if (isValid) setDuplicatesCheckType('submit');
    }, [getIsValidCheck, handleSaveAndSubmitRequest, request.integrationCode]);

    const duplicateCheckReSubmit = useCallback(async () => {
        if (!hasDuplicateCheck(request.integrationCode)) {
            resubmitRequestPopup();

            return;
        }

        const isValid = await getIsValidCheck();

        if (isValid) setDuplicatesCheckType('resubmit');
    }, [getIsValidCheck, resubmitRequestPopup, request.integrationCode]);

    const handleCancelActivePopup = useCallback(() => {
        dispatch(cancelActivePopup());

        if (activePopupId === OPTIMISTIC_LOCKING_POPUP) {
            setNeedToCompleteReviewFlag(false);
            setResubmitCommentText('');
        }
    }, [activePopupId, dispatch]);

    const handleResubmitRequest = useCallback(
        ({ commentText, needToCompleteReview, isOverwriteRequest }: ResubmitRequestParams) => {
            setSubmitRequestType(SubmitRequestType.Resubmit);
            setResubmitCommentText(commentText);
            ocrAvailableRequest && setDefaultSplitViewOpen(openSplitView);

            if (needToCompleteReview) {
                setNeedToCompleteReviewFlag(true);
            }

            if (customSaveAndSubmitRequest) {
                customSaveAndSubmitRequest(commentText, needToCompleteReview);
            } else {
                dispatch(
                    saveAndResubmitRequest({
                        commentText,
                        customGetRequestTransfer,
                        customRequestValidationFn,
                        needToCompleteReview,
                        isOverwriteRequest,
                    })
                );
            }

            handleCancelActivePopup();

            invalidateDuplicates();
        },
        [
            customGetRequestTransfer,
            customRequestValidationFn,
            customSaveAndSubmitRequest,
            dispatch,
            handleCancelActivePopup,
            invalidateDuplicates,
            ocrAvailableRequest,
            openSplitView,
            setDefaultSplitViewOpen,
        ]
    );

    const saveChanges = useCallback(() => {
        handleResubmitRequest({ commentText: '' });
    }, [handleResubmitRequest]);

    const handleResubmitAndCompleteReview = useCallback(() => {
        handleResubmitRequest({ commentText: '', needToCompleteReview: true });
    }, [handleResubmitRequest]);

    const handleDeleteRequest = useCallback(() => {
        dispatch(deleteRequest());
    }, [dispatch]);

    const handleSaveDraftRequest = useCallback(() => {
        ocrAvailableRequest && setDefaultSplitViewOpen(openSplitView);
        dispatch(saveDraftRequest({ customGetRequestTransfer }));

        if (request.integrationCode === domain.IntegrationCode.XeroAmaxPayBatchPayment) {
            invalidateGetAmaxPayXeroRequestDetails({
                path: {
                    companyId: request.companyId,
                    requestId: request.id,
                },
            });
        }
    }, [
        ocrAvailableRequest,
        setDefaultSplitViewOpen,
        openSplitView,
        dispatch,
        customGetRequestTransfer,
        request.integrationCode,
        request.companyId,
        request.id,
        invalidateGetAmaxPayXeroRequestDetails,
    ]);

    const onSaveAndApproveRequest = useCallback(
        (isOverwriteRequest?: boolean) => {
            ocrAvailableRequest && setDefaultSplitViewOpen(openSplitView);
            setSubmitRequestType(SubmitRequestType.SaveAndApprove);
            dispatch(saveAndApproveRequest({ isOverwriteRequest }));

            invalidateDuplicates();
        },
        [dispatch, invalidateDuplicates, ocrAvailableRequest, openSplitView, setDefaultSplitViewOpen]
    );

    const handleSaveAndApproveRequest = useCallback(() => {
        onSaveAndApproveRequest();
    }, [onSaveAndApproveRequest]);

    const handleOverwriteRequest = useCallback(() => {
        switch (submitRequestType) {
            case SubmitRequestType.SaveAndApprove:
                onSaveAndApproveRequest(true);
                break;

            case SubmitRequestType.Resubmit: {
                handleResubmitRequest({
                    commentText: resubmitCommentText,
                    isOverwriteRequest: true,
                    needToCompleteReview: needToCompleteReviewFlag,
                });

                setNeedToCompleteReviewFlag(false);
                setResubmitCommentText('');
                break;
            }

            default:
                return;
        }

        setSubmitRequestType(null);
    }, [
        submitRequestType,
        onSaveAndApproveRequest,
        handleResubmitRequest,
        resubmitCommentText,
        needToCompleteReviewFlag,
    ]);

    const { confirmation } = useConfirmation({
        confirmTitle: messages.removeRequestTitle,
        confirmMessage: messages.deleteConfirmationText,
        confirmButtonColor: 'red40',
    });

    const handleConfirmDelete = useCallback(() => {
        void confirmation()
            .then(handleDeleteRequest)
            .catch(() => {});
    }, [confirmation, handleDeleteRequest]);

    const { isBelgradeUIResubmitRequestPopup, isBelgradeUICheckDuplicatePopup } = useUserBetaFeatures();
    const { isPageRedesign } = usePageRedesign({ exclude: [domain.IntegrationCode.XeroContact] });

    return (
        <>
            {isPageRedesign ? (
                <Grid gap={16} gridAutoFlow='column'>
                    {!commands.delete.hidden && (
                        <Button
                            onClick={handleConfirmDelete}
                            disabled={commands.delete.disabled || loading || isSaving}
                            title={messages.removeRequestTitle}
                            size='small'
                            color='red100'
                            icon
                            noPadding
                            data-qa={qa('delete-button')}
                        >
                            <DeleteIcon />
                        </Button>
                    )}

                    {!commands.saveDraft.hidden && (
                        <Button
                            onClick={handleSaveDraftRequest}
                            disabled={commands.saveDraft.disabled || loading || isSaving || ocrIsLoading}
                            title={messages.saveDraftTitle}
                            size='small'
                            icon
                            noPadding
                            data-qa={qa('save-button')}
                        >
                            <SaveIcon />
                        </Button>
                    )}

                    {!isImportFileButtonHidden && onOpenImportFilePopup && (
                        <Button
                            onClick={onOpenImportFilePopup}
                            disabled={loading || isSaving}
                            title={messages.importFileTitle}
                            size='small'
                            color='midnight80'
                            variant='outline'
                            data-qa={qa('import-button')}
                        >
                            {messages.importFileTitle}
                        </Button>
                    )}

                    {!commands.saveAndSubmit.hidden && (
                        <Button
                            onClick={duplicateCheckSubmit}
                            disabled={commands.saveAndSubmit.disabled || loading || isSaving || ocrIsLoading}
                            title={messages.saveAndSubmitTitle}
                            size='small'
                            color='mint80'
                            data-qa={qa('submit-button')}
                        >
                            {messages.saveAndSubmitText}
                        </Button>
                    )}

                    {!commands.saveAndResubmit.hidden && (
                        <Button
                            onClick={duplicateCheckReSubmit}
                            disabled={commands.saveAndResubmit.disabled || loading || isSaving}
                            title={messages.saveAndResubmitTitle}
                            size='small'
                            color='mint80'
                            data-qa={qa('resubmit-button')}
                        >
                            {messages.saveAndResubmitText}
                        </Button>
                    )}

                    {!commands.saveAndApprove.hidden && (
                        <Button
                            onClick={handleSaveAndApproveRequest}
                            disabled={commands.saveAndApprove.disabled || loading || isSaving}
                            title={messages.saveAndApproveTitle}
                            size='small'
                            color='mint80'
                            data-qa={qa('save-and-approve-button')}
                        >
                            {messages.saveAndApproveText}
                        </Button>
                    )}

                    {!commands.saveChanges.hidden && (
                        <Button
                            onClick={saveChanges}
                            disabled={commands.saveChanges.disabled || loading || isSaving}
                            title={messages.saveChanges}
                            size='small'
                            color='midnight80'
                            variant='outline'
                            data-qa={qa('save-changes-button')}
                        >
                            {messages.saveChanges}
                        </Button>
                    )}

                    {!commands.saveAndCompleteReview.hidden && (
                        <Button
                            onClick={handleResubmitAndCompleteReview}
                            disabled={commands.saveAndCompleteReview.disabled || loading || isSaving}
                            title={messages.saveChangesAndSubmitForApproval}
                            size='small'
                            color='mint80'
                            data-qa={qa('save-and-complete-review-button')}
                        >
                            {messages.saveChangesAndSubmitForApproval}
                        </Button>
                    )}
                </Grid>
            ) : (
                <Container className={className}>
                    <ConfirmationDropdown confirmation={messages.deleteConfirmationText}>
                        <ToolbarButton
                            qa={qa('delete-button')}
                            execute={handleDeleteRequest}
                            disabled={loading || isSaving}
                            theme='dark'
                            command={commands.delete}
                            title={messages.removeRequestTitle}
                        >
                            <TbDeleteIcon width={16} height={17} />
                        </ToolbarButton>
                    </ConfirmationDropdown>

                    <ToolbarButton
                        qa={qa('save-button')}
                        execute={handleSaveDraftRequest}
                        command={commands.saveDraft}
                        disabled={loading || isSaving || ocrIsLoading}
                        theme='dark'
                        title={messages.saveDraftTitle}
                    >
                        <TbSaveColorlessIcon width={19} height={18} />
                    </ToolbarButton>

                    {!isImportFileButtonHidden && onOpenImportFilePopup && (
                        <ImportButton>
                            <ButtonOld
                                theme='secondary'
                                execute={onOpenImportFilePopup}
                                disabled={loading || isSaving}
                                title={messages.importFileTitle}
                                padding='narrow10'
                            >
                                {messages.importFileTitle}
                            </ButtonOld>
                        </ImportButton>
                    )}

                    <ButtonOld
                        qa={qa('submit-button')}
                        execute={duplicateCheckSubmit}
                        command={commands.saveAndSubmit}
                        disabled={loading || isSaving || ocrIsLoading}
                        title={messages.saveAndSubmitTitle}
                    >
                        {messages.saveAndSubmitText}
                    </ButtonOld>

                    <ButtonOld
                        qa={qa('resubmit-button')}
                        execute={duplicateCheckReSubmit}
                        command={commands.saveAndResubmit}
                        disabled={loading || isSaving}
                        title={messages.saveAndResubmitTitle}
                    >
                        {messages.saveAndResubmitText}
                    </ButtonOld>

                    <ButtonOld
                        qa={qa('save-and-approve-button')}
                        execute={handleSaveAndApproveRequest}
                        command={commands.saveAndApprove}
                        disabled={loading || isSaving}
                        title={messages.saveAndApproveTitle}
                    >
                        {messages.saveAndApproveText}
                    </ButtonOld>

                    <ButtonOld
                        qa={qa('save-changes-button')}
                        execute={saveChanges}
                        command={commands.saveChanges}
                        disabled={loading || isSaving}
                        title={messages.saveChanges}
                        theme='secondary'
                    >
                        {messages.saveChanges}
                    </ButtonOld>

                    <ButtonOld
                        qa={qa('save-and-complete-review-button')}
                        execute={handleResubmitAndCompleteReview}
                        command={commands.saveAndCompleteReview}
                        disabled={loading || isSaving}
                        title={messages.saveChangesAndSubmitForApproval}
                    >
                        {messages.saveChangesAndSubmitForApproval}
                    </ButtonOld>
                </Container>
            )}

            {isBelgradeUIResubmitRequestPopup ? (
                <Popup size='small' open={activePopupId === RESUBMIT_REQUEST_POPUP} onClose={handleCancelActivePopup}>
                    <ResubmitRequestPopupContent onSubmit={handleResubmitRequest} />
                </Popup>
            ) : (
                <PopupOld isOpen={activePopupId === RESUBMIT_REQUEST_POPUP} onRequestClose={handleCancelActivePopup}>
                    <ResubmitRequestPopup resubmitRequest={handleResubmitRequest} />
                </PopupOld>
            )}

            <OptimisticLockingPopup onOverwriteRequest={handleOverwriteRequest} onClose={handleCancelActivePopup} />

            {isBelgradeUICheckDuplicatePopup ? (
                duplicatesCheckType && (
                    <CheckDuplicatesPopup
                        onCancel={onDuplicateCheckCancel}
                        onSubmit={duplicatesCheckType === 'submit' ? handleSaveAndSubmitRequest : resubmitRequestPopup}
                    />
                )
            ) : (
                <>
                    {request.integrationCode === IntegrationCode.XeroBill && duplicatesCheckType === 'submit' && (
                        <CheckDuplicatesXero
                            onCancel={onDuplicateCheckCancel}
                            onSuccess={handleSaveAndSubmitRequest}
                            onSuccessText={messages.submitButton}
                            companyId={request.companyId}
                        />
                    )}

                    {request.integrationCode === IntegrationCode.XeroBill && duplicatesCheckType === 'resubmit' && (
                        <CheckDuplicatesXero
                            onCancel={onDuplicateCheckCancel}
                            onSuccess={resubmitRequestPopup}
                            onSuccessText={messages.resubmitButton}
                            companyId={request.companyId}
                            currentRequestId={request.id}
                        />
                    )}

                    {request.integrationCode === IntegrationCode.QBooksBill && duplicatesCheckType === 'submit' && (
                        <CheckDuplicatesQbooks
                            onCancel={onDuplicateCheckCancel}
                            onSuccess={handleSaveAndSubmitRequest}
                            onSuccessText={messages.submitButton}
                            companyId={request.companyId}
                        />
                    )}

                    {request.integrationCode === IntegrationCode.QBooksBill && duplicatesCheckType === 'resubmit' && (
                        <CheckDuplicatesQbooks
                            onCancel={onDuplicateCheckCancel}
                            onSuccess={resubmitRequestPopup}
                            onSuccessText={messages.resubmitButton}
                            companyId={request.companyId}
                            currentRequestId={request.id}
                        />
                    )}

                    {request.integrationCode === IntegrationCode.NetSuiteBill && duplicatesCheckType === 'submit' && (
                        <CheckDuplicatesNetSuite
                            onCancel={onDuplicateCheckCancel}
                            onSuccess={handleSaveAndSubmitRequest}
                            onSuccessText={messages.submitButton}
                            companyId={request.companyId}
                        />
                    )}

                    {request.integrationCode === IntegrationCode.NetSuiteBill && duplicatesCheckType === 'resubmit' && (
                        <CheckDuplicatesNetSuite
                            onCancel={onDuplicateCheckCancel}
                            onSuccess={resubmitRequestPopup}
                            onSuccessText={messages.resubmitButton}
                            companyId={request.companyId}
                            currentRequestId={request.id}
                        />
                    )}
                </>
            )}
        </>
    );
});

ActionsBlock.displayName = 'ActionsBlock';

export default ActionsBlock;
